import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './MarketShipmentsPage.css';

// Ship-to-customer orders from the Market POS. Each ticket has a slip in the
// box with its SHIP-#### number; the customer fills in the address from a QR
// code at the register. Here the shipping team checks the address, enters the
// weight, prints the UPS label (same account and Zebra printers as Quick
// Shipment), and marks it shipped.
//
// Once the box is packed with its slip, a store lead clicks "Packed and sent
// to warehouse" so the warehouse knows it's coming.
//
// The slip also has a QR code (?checkin=SHIP-0012). Scanning it with a phone,
// or a handheld scanner into the search box, checks the box in as arrived at
// the warehouse.

const STATUS_LABELS = {
  awaiting_info: 'Waiting on customer',
  ready: 'Ready to ship',
  label_created: 'Label printed',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
};

const SERVICES = [
  { code: '03', label: 'UPS Ground' },
  { code: '12', label: 'UPS 3 Day Select' },
  { code: '02', label: 'UPS 2nd Day Air' },
  { code: '01', label: 'UPS Next Day Air' },
];

const ADDRESS_FIELDS = [
  ['customer_name', 'Name'],
  ['customer_email', 'Email'],
  ['customer_phone', 'Phone'],
  ['address1', 'Street address'],
  ['address2', 'Apt / suite'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
];

const EMPTY_MANUAL = {
  kind: 'replacement',
  reason: '',
  original_order: '',
  location: 'chs_store',
  items: [{ sku: '', title: '', quantity: '1' }],
};

// Entered by hand (a replacement, a missed shipment) rather than from a sale
const isManual = (s) => !s.sale_id;

// A scanned QR comes through as the whole URL; a typed one as just the number
function ticketFrom(text) {
  const m = String(text || '').match(/SHIP-\d+/i);
  return m ? m[0].toUpperCase() : null;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function downloadZpl(zpl, trackingNumber) {
  const blob = new Blob([zpl], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `label-${trackingNumber || 'shipping'}.zpl`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Send ZPL to the Zebra via the server when this browser has a printer address
// set; otherwise (or if it can't be reached) download the .zpl, which the
// shipping PCs print straight to the Zebra.
async function printLabel(labelBase64, trackingNumber) {
  const zpl = atob(labelBase64);
  const printerIp = localStorage.getItem('zebraPrinterIp');
  if (printerIp) {
    try {
      await axios.post('/api/print/zpl', { zpl, printerIp });
      return 'printed';
    } catch (err) {
      console.error('Network print failed:', err.response?.data?.error || err.message);
    }
  }
  downloadZpl(zpl, trackingNumber);
  return 'downloaded';
}

export default function MarketShipmentsPage() {
  const [view, setView] = useState('open');
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState({});
  const [pkg, setPkg] = useState({ weight: '', length: '12', width: '10', height: '8', serviceCode: '03' });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [manual, setManual] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const checkinHandled = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/market-shipments?view=${view}`);
      setShipments(data.shipments || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load shipments' });
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const selected = shipments.find((s) => s.id === selectedId) || null;

  useEffect(() => {
    if (!selected) return;
    const d = {};
    for (const [k] of ADDRESS_FIELDS) d[k] = selected[k] || '';
    d.delivery_notes = selected.delivery_notes || '';
    d.staff_notes = selected.staff_notes || '';
    setDraft(d);
    setPkg((p) => ({ ...p, weight: selected.package_weight_lbs ? String(selected.package_weight_lbs) : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const replace = (updated) => setShipments((list) => list.map((s) => (s.id === updated.id ? updated : s)));

  const run = async (fn) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveAddress = () => run(async () => {
    const { data } = await axios.patch(`/api/market-shipments/${selected.id}`, draft);
    replace(data.shipment);
    setMessage({ type: 'ok', text: 'Saved.' });
  });

  const createLabel = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/label`, pkg);
    replace(data.shipment);
    const how = await printLabel(data.labelData, data.trackingNumber);
    const emailNote = data.email?.sent
      ? ` Tracking emailed to ${data.shipment.customer_email}.`
      : ` Tracking email NOT sent${data.email?.error ? ` (${data.email.error})` : data.email?.skipped ? ` (${data.email.skipped})` : ''}; use Resend below.`;
    setMessage({
      type: data.email?.sent ? 'ok' : 'error',
      text: (how === 'printed'
        ? `Label printed. Tracking ${data.trackingNumber}.`
        : `Label sent to the printer (tracking ${data.trackingNumber}).`) + emailNote,
    });
  });

  const resendEmail = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/email-tracking`);
    replace(data.shipment);
    setMessage({ type: 'ok', text: `Tracking emailed to ${data.shipment.customer_email}.` });
  });

  const reprintLabel = () => run(async () => {
    const { data } = await axios.get(`/api/market-shipments/${selected.id}/label`);
    await printLabel(data.labelData, data.trackingNumber);
    setMessage({ type: 'ok', text: 'Label sent to the printer.' });
  });

  const voidLabel = () => {
    if (!window.confirm(`Void UPS label ${selected.tracking_number}? The ticket goes back to Ready to ship.`)) return;
    run(async () => {
      const { data } = await axios.post(`/api/market-shipments/${selected.id}/void-label`);
      replace(data.shipment);
      setMessage({ type: 'ok', text: 'Label voided.' });
    });
  };

  // Scan = check in. Looks the ticket up even if it isn't in the current tab.
  const checkIn = (ticket) => run(async () => {
    const { data: found } = await axios.get(`/api/market-shipments/by-ticket/${encodeURIComponent(ticket)}`);
    setShipments((list) => (list.some((s) => s.id === found.shipment.id) ? list : [found.shipment, ...list]));
    setManual(null);
    setSelectedId(found.shipment.id);
    const { data } = await axios.post(`/api/market-shipments/${found.shipment.id}/received`);
    replace(data.shipment);
    setMessage({
      type: 'ok',
      text: data.alreadyReceived
        ? `${ticket} was already checked in ${formatDate(data.shipment.received_at)}.`
        : `${ticket} checked in at the warehouse.`,
    });
  });

  const markSent = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/sent`);
    replace(data.shipment);
    setMessage({
      type: 'ok',
      text: data.alreadySent
        ? `${data.shipment.ticket_number} was already marked sent ${formatDate(data.shipment.sent_to_warehouse_at)}.`
        : `${data.shipment.ticket_number} marked sent to the warehouse.`,
    });
  });

  const undoSent = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/sent`, { undo: true });
    replace(data.shipment);
    setMessage({ type: 'ok', text: `Sent to warehouse removed for ${data.shipment.ticket_number}.` });
  });

  const undoCheckIn = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/received`, { undo: true });
    replace(data.shipment);
    setMessage({ type: 'ok', text: `Check-in removed for ${data.shipment.ticket_number}.` });
  });

  // Opened from the QR code on the slip
  useEffect(() => {
    const ticket = ticketFrom(searchParams.get('checkin'));
    if (!ticket || checkinHandled.current) return;
    checkinHandled.current = true;
    setSearchParams({}, { replace: true });
    checkIn(ticket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const markShipped = () => run(async () => {
    const { data } = await axios.post(`/api/market-shipments/${selected.id}/shipped`);
    replace(data.shipment);
    setMessage({ type: 'ok', text: `${data.shipment.ticket_number} marked shipped.` });
  });

  const startManual = () => {
    setSelectedId(null);
    setMessage(null);
    setManual({ ...EMPTY_MANUAL, items: [{ ...EMPTY_MANUAL.items[0] }] });
    setDraft(Object.fromEntries(ADDRESS_FIELDS.map(([k]) => [k, ''])));
  };

  const setManualItem = (i, k, v) => setManual((m) => ({
    ...m,
    items: m.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)),
  }));

  const createManual = () => run(async () => {
    const { data } = await axios.post('/api/market-shipments', {
      ...manual,
      ...draft,
      items: manual.items.filter((it) => it.title.trim() || it.sku.trim()),
    });
    setManual(null);
    if (view === 'shipped') setView('open');
    setShipments((list) => [...list.filter((s) => s.id !== data.shipment.id), data.shipment]);
    setSelectedId(data.shipment.id);
    setMessage({
      type: 'ok',
      text: data.shipment.status === 'ready'
        ? `${data.shipment.ticket_number} created and ready to ship. Write ${data.shipment.ticket_number} on the box.`
        : `${data.shipment.ticket_number} created. Add the address below before printing a label.`,
    });
  });

  const cancelTicket = () => {
    if (!window.confirm(`Cancel ${selected.ticket_number}? Use this only if the customer is not having it shipped after all.`)) return;
    run(async () => {
      const { data } = await axios.patch(`/api/market-shipments/${selected.id}`, { status: 'cancelled' });
      replace(data.shipment);
      setMessage({ type: 'ok', text: 'Ticket cancelled.' });
    });
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? shipments.filter((s) => [s.ticket_number, s.customer_name, s.sale_number, s.tracking_number]
        .some((v) => (v || '').toLowerCase().includes(q)))
    : shipments;

  const addressLocked = selected && !['awaiting_info', 'ready'].includes(selected.status);
  // A sale shipping to several addresses has one ticket per destination
  const siblingCount = (s) => shipments.filter((x) => x.sale_id && x.sale_id === s.sale_id && x.status !== 'cancelled').length;

  return (
    <div className="market-ship-page">
      <div className="market-ship-header">
        <div>
          <h1>Candlefish CHS Store Shipments</h1>
          <p className="subtitle">Ship-to-customer orders rung up at the Charleston store and Makers Market. Match the SHIP number on the slip in the box.</p>
        </div>
        <div className="market-ship-tabs">
          {[['open', 'Open'], ['shipped', 'Shipped'], ['all', 'All']].map(([k, label]) => (
            <button key={k} className={view === k ? 'active' : ''} onClick={() => { setView(k); setSelectedId(null); }}>
              {label}
            </button>
          ))}
          <button onClick={load} disabled={loading}>Refresh</button>
          <button className="new-shipment" onClick={startManual}>New shipment</button>
        </div>
      </div>

      <div className="market-ship-body">
        <div className="market-ship-list">
          <input
            className="market-ship-search"
            placeholder="Scan or type SHIP number, name, sale..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const ticket = ticketFrom(search);
              if (ticket) {
                setSearch('');
                checkIn(ticket);
              } else if (visible.length === 1) {
                setSelectedId(visible[0].id);
              }
            }}
          />
          {loading && <p className="muted">Loading...</p>}
          {!loading && visible.length === 0 && <p className="muted">Nothing here.</p>}
          {visible.map((s) => (
            <button
              key={s.id}
              className={`market-ship-row ${s.id === selectedId ? 'selected' : ''}`}
              onClick={() => { setSelectedId(s.id); setManual(null); setMessage(null); }}
            >
              <div className="row-top">
                <span className="ticket">
                  {s.ticket_number}
                  {isManual(s) && <span className="manual-tag">Entered by hand</span>}
                </span>
                <span className={`status status-${s.status}`}>{STATUS_LABELS[s.status]}</span>
              </div>
              {s.received_at
                ? <div className="row-received">At warehouse &middot; {formatDate(s.received_at)}</div>
                : s.sent_to_warehouse_at
                  ? <div className="row-received">Sent to warehouse &middot; {formatDate(s.sent_to_warehouse_at)}</div>
                  : 'sent_to_warehouse_at' in s && ['awaiting_info', 'ready'].includes(s.status) && <div className="row-not-sent">Not sent to warehouse yet</div>}
              <div className="row-sub">
                {s.customer_name || 'No address yet'} &middot; {formatDate(s.created_at)}
              </div>
              {s.checkout_notes && <div className="row-sub">{s.checkout_notes}</div>}
            </button>
          ))}
        </div>

        <div className="market-ship-detail">
          {manual && (
            <>
              <div className="detail-head">
                <div>
                  <h2>New shipment</h2>
                  <p className="muted">For a replacement or anything else that needs to ship and wasn't rung up with shipping. It joins this queue like any other ticket.</p>
                </div>
              </div>

              {message && <div className={`market-ship-msg ${message.type}`}>{message.text}</div>}

              <section>
                <h3>Why it's shipping</h3>
                <div className="address-grid">
                  <label>
                    <span>Type</span>
                    <select value={manual.kind} onChange={(e) => setManual((m) => ({ ...m, kind: e.target.value }))}>
                      <option value="replacement">Replacement</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label>
                    <span>From</span>
                    <select value={manual.location} onChange={(e) => setManual((m) => ({ ...m, location: e.target.value }))}>
                      <option value="chs_store">CHS Store</option>
                      <option value="market">Makers Market</option>
                    </select>
                  </label>
                  <label className="wide">
                    <span>Reason (the warehouse sees this)</span>
                    <input
                      value={manual.reason}
                      placeholder="e.g. Candle arrived broken, sending a new one"
                      onChange={(e) => setManual((m) => ({ ...m, reason: e.target.value }))}
                    />
                  </label>
                  <label className="wide">
                    <span>Original sale or order number (optional)</span>
                    <input
                      value={manual.original_order}
                      placeholder="e.g. STORE-8777 or #361378"
                      onChange={(e) => setManual((m) => ({ ...m, original_order: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              <section>
                <h3>In the box</h3>
                {manual.items.map((it, i) => (
                  <div key={i} className="manual-item">
                    <input
                      className="qty"
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => setManualItem(i, 'quantity', e.target.value)}
                      aria-label="Quantity"
                    />
                    <input
                      className="title"
                      value={it.title}
                      placeholder="Item"
                      onChange={(e) => setManualItem(i, 'title', e.target.value)}
                    />
                    <input
                      className="sku"
                      value={it.sku}
                      placeholder="SKU (optional)"
                      onChange={(e) => setManualItem(i, 'sku', e.target.value)}
                    />
                    {manual.items.length > 1 && (
                      <button
                        className="link-button"
                        onClick={() => setManual((m) => ({ ...m, items: m.items.filter((_, j) => j !== i) }))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  className="link-button"
                  onClick={() => setManual((m) => ({ ...m, items: [...m.items, { sku: '', title: '', quantity: '1' }] }))}
                >
                  Add another item
                </button>
              </section>

              <section>
                <h3>Ship to</h3>
                <div className="address-grid">
                  {ADDRESS_FIELDS.map(([k, label]) => (
                    <label key={k} className={k === 'address1' || k === 'customer_email' ? 'wide' : ''}>
                      <span>{label}</span>
                      <input
                        value={draft[k] || ''}
                        disabled={busy}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <label className="wide">
                    <span>Delivery notes</span>
                    <input
                      value={draft.delivery_notes || ''}
                      disabled={busy}
                      onChange={(e) => setDraft((d) => ({ ...d, delivery_notes: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              <div className="button-row">
                <button
                  className="primary"
                  onClick={createManual}
                  disabled={busy || !manual.reason.trim() || !manual.items.some((it) => it.title.trim() || it.sku.trim())}
                >
                  {busy ? 'Working...' : 'Create shipment'}
                </button>
                <button className="secondary" onClick={() => { setManual(null); setMessage(null); }} disabled={busy}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {!manual && !selected && <p className="muted">Select a ticket.</p>}
          {!manual && selected && (
            <>
              <div className="detail-head">
                <div>
                  <h2>{selected.ticket_number}</h2>
                  <p className="muted">
                    {isManual(selected)
                      ? <>Entered by hand{selected.sale_number && <> &middot; for {selected.sale_number}</>}</>
                      : <>Sale {selected.sale_number}</>} &middot; {selected.location === 'chs_store' ? 'CHS Store' : 'Makers Market'} &middot; {formatDate(selected.created_at)}
                    {siblingCount(selected) > 1 && <> &middot; shipment {selected.shipment_index} of {siblingCount(selected)}</>}
                  </p>
                </div>
                <span className={`status status-${selected.status}`}>{STATUS_LABELS[selected.status]}</span>
              </div>

              {message && <div className={`market-ship-msg ${message.type}`}>{message.text}</div>}

              {selected.status !== 'cancelled' && (
                <section className="received-line">
                  {selected.sent_to_warehouse_at ? (
                    <p>
                      Sent to warehouse {formatDate(selected.sent_to_warehouse_at)}
                      {selected.sent_to_warehouse_by && <span className="muted"> &middot; {selected.sent_to_warehouse_by}</span>}
                      {' '}<button className="link-button" onClick={undoSent} disabled={busy}>Undo</button>
                    </p>
                  ) : !selected.received_at && (
                    <p>
                      <button className="primary" onClick={markSent} disabled={busy}>
                        Packed and sent to warehouse
                      </button>
                    </p>
                  )}
                  {selected.received_at ? (
                    <p>
                      Arrived at warehouse {formatDate(selected.received_at)}
                      {selected.received_by && <span className="muted"> &middot; {selected.received_by}</span>}
                      {' '}<button className="link-button" onClick={undoCheckIn} disabled={busy}>Undo</button>
                    </p>
                  ) : (
                    <button className="secondary" onClick={() => checkIn(selected.ticket_number)} disabled={busy}>
                      Check in: box arrived at warehouse
                    </button>
                  )}
                </section>
              )}

              {selected.checkout_notes && (
                <section>
                  <h3>Notes from checkout</h3>
                  <p className="checkout-notes">{selected.checkout_notes}</p>
                </section>
              )}

              <section>
                <h3>{siblingCount(selected) > 1 ? 'Items on this sale (see notes for this box)' : 'In the box'}</h3>
                {(selected.items || []).length === 0 && <p className="muted">No items recorded.</p>}
                {(selected.items || []).map((it, i) => (
                  <div key={i} className="item-line">
                    <span>{it.quantity > 1 ? `${it.quantity} x ` : ''}{it.title}</span>
                    <span className="muted">{it.sku}</span>
                  </div>
                ))}
              </section>

              <section>
                <h3>Ship to</h3>
                {selected.status === 'awaiting_info' && (
                  <p className="muted">The customer hasn't filled in the form yet. You can enter the address here if they gave it another way.</p>
                )}
                <div className="address-grid">
                  {ADDRESS_FIELDS.map(([k, label]) => (
                    <label key={k} className={k === 'address1' || k === 'customer_email' ? 'wide' : ''}>
                      <span>{label}</span>
                      <input
                        value={draft[k] || ''}
                        disabled={addressLocked || busy}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <label className="wide">
                    <span>Delivery notes</span>
                    <input
                      value={draft.delivery_notes || ''}
                      disabled={addressLocked || busy}
                      onChange={(e) => setDraft((d) => ({ ...d, delivery_notes: e.target.value }))}
                    />
                  </label>
                  <label className="wide">
                    <span>Staff notes</span>
                    <input
                      value={draft.staff_notes || ''}
                      disabled={busy}
                      onChange={(e) => setDraft((d) => ({ ...d, staff_notes: e.target.value }))}
                    />
                  </label>
                </div>
                <button className="secondary" onClick={saveAddress} disabled={busy}>Save changes</button>
              </section>

              {selected.status === 'ready' && (
                <section>
                  <h3>Package</h3>
                  <div className="package-grid">
                    <label><span>Weight (lbs)</span>
                      <input type="number" step="0.1" min="0" value={pkg.weight} onChange={(e) => setPkg((p) => ({ ...p, weight: e.target.value }))} />
                    </label>
                    <label><span>L (in)</span>
                      <input type="number" value={pkg.length} onChange={(e) => setPkg((p) => ({ ...p, length: e.target.value }))} />
                    </label>
                    <label><span>W (in)</span>
                      <input type="number" value={pkg.width} onChange={(e) => setPkg((p) => ({ ...p, width: e.target.value }))} />
                    </label>
                    <label><span>H (in)</span>
                      <input type="number" value={pkg.height} onChange={(e) => setPkg((p) => ({ ...p, height: e.target.value }))} />
                    </label>
                    <label className="wide"><span>Service</span>
                      <select value={pkg.serviceCode} onChange={(e) => setPkg((p) => ({ ...p, serviceCode: e.target.value }))}>
                        {SERVICES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                      </select>
                    </label>
                  </div>
                  <button className="primary" onClick={createLabel} disabled={busy || !pkg.weight}>
                    {busy ? 'Working...' : 'Create & Print UPS Label'}
                  </button>
                </section>
              )}

              {(selected.status === 'label_created' || selected.status === 'shipped') && (
                <section>
                  <h3>Label</h3>
                  <p>
                    Tracking <strong>{selected.tracking_number}</strong>
                    {selected.label_cost != null && <span className="muted"> &middot; ${Number(selected.label_cost).toFixed(2)}</span>}
                    {selected.shipped_at && <span className="muted"> &middot; shipped {formatDate(selected.shipped_at)}</span>}
                  </p>
                  <p className={selected.tracking_emailed_at && selected.tracking_emailed_number === selected.tracking_number ? 'muted' : 'email-warn'}>
                    {selected.tracking_emailed_at && selected.tracking_emailed_number === selected.tracking_number
                      ? `Tracking emailed to ${selected.customer_email} on ${formatDate(selected.tracking_emailed_at)}.`
                      : `Tracking email not sent${selected.tracking_email_error ? `: ${selected.tracking_email_error}` : '.'}`}
                  </p>
                  <div className="button-row">
                    <button className="secondary" onClick={reprintLabel} disabled={busy}>Reprint label</button>
                    <button className="secondary" onClick={resendEmail} disabled={busy || !selected.customer_email}>
                      {selected.tracking_emailed_at ? 'Resend tracking email' : 'Send tracking email'}
                    </button>
                    {selected.status === 'label_created' && (
                      <>
                        <button className="primary" onClick={markShipped} disabled={busy}>Mark shipped</button>
                        <button className="danger" onClick={voidLabel} disabled={busy}>Void label</button>
                      </>
                    )}
                  </div>
                </section>
              )}

              {['awaiting_info', 'ready'].includes(selected.status) && (
                <button className="link-danger" onClick={cancelTicket} disabled={busy}>Cancel this ticket</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
